import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Calculator, 
  Leaf, 
  TreePine, 
  DollarSign, 
  X,
  CheckCircle,
  ArrowRight,
  Loader2,
  Ruler,
  Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const farmingPractices = [
  { id: 'agroforesterie', label: 'Agroforesterie', description: 'Arbres et cultures ensemble' },
  { id: 'compost', label: 'Compost organique', description: 'Engrais naturel' },
  { id: 'zero_pesticides', label: 'Zero pesticides chimiques', description: 'Agriculture propre' },
  { id: 'couverture_vegetale', label: 'Couverture vegetale', description: 'Protection du sol' },
  { id: 'rotation_cultures', label: 'Rotation des cultures', description: 'Diversification' },
  { id: 'zero_brulage', label: 'Zero brulage des residus', description: 'Compostage a la place (-1.5 pts si brulage)' },
];

const CarbonCalculator = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [calculating, setCalculating] = useState(false);
  const [formData, setFormData] = useState({
    hectares: '',
    arbres_grands: '',
    arbres_moyens: '',
    arbres_petits: '',
    // Diamètres de couronne (défauts scientifiques basés sur littérature ICRAF)
    diametre_grands_m: '12',
    diametre_moyens_m: '8',
    diametre_petits_m: '4',
    practices: [],
    cropType: 'cacao'
  });
  const [result, setResult] = useState(null);

  // Couverture d'ombrage en temps réel (basée sur diamètres saisis)
  const liveCoverage = (() => {
    const ha = parseFloat(formData.hectares) || 0;
    if (ha <= 0) return null;
    const ng = parseInt(formData.arbres_grands) || 0;
    const nm = parseInt(formData.arbres_moyens) || 0;
    const np = parseInt(formData.arbres_petits) || 0;
    const dg = parseFloat(formData.diametre_grands_m) || 12;
    const dm = parseFloat(formData.diametre_moyens_m) || 8;
    const dp = parseFloat(formData.diametre_petits_m) || 4;
    const areaG = Math.PI * Math.pow(dg / 2, 2);
    const areaM = Math.PI * Math.pow(dm / 2, 2);
    const areaP = Math.PI * Math.pow(dp / 2, 2);
    const totalCrown = ng * areaG + nm * areaM + np * areaP;
    const pct = (totalCrown / (ha * 10000)) * 100;
    return Math.min(Math.round(pct * 10) / 10, 100);
  })();

  const togglePractice = (practiceId) => {
    setFormData(prev => ({
      ...prev,
      practices: prev.practices.includes(practiceId)
        ? prev.practices.filter(p => p !== practiceId)
        : [...prev.practices, practiceId]
    }));
  };

  const totalTrees = (parseInt(formData.arbres_grands) || 0) + (parseInt(formData.arbres_moyens) || 0) + (parseInt(formData.arbres_petits) || 0);

  const calculatePremium = async () => {
    setCalculating(true);
    try {
      const response = await fetch(`${API_URL}/api/ussd/calculate-premium`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hectares: parseFloat(formData.hectares) || 1,
          arbres_grands: parseInt(formData.arbres_grands) || 0,
          arbres_moyens: parseInt(formData.arbres_moyens) || 0,
          arbres_petits: parseInt(formData.arbres_petits) || 0,
          diametre_grands_m: parseFloat(formData.diametre_grands_m) || 12,
          diametre_moyens_m: parseFloat(formData.diametre_moyens_m) || 8,
          diametre_petits_m: parseFloat(formData.diametre_petits_m) || 4,
          culture: formData.cropType,
          practices: formData.practices
        })
      });
      const data = await response.json();
      setResult(data);
      setStep(3);
    } catch (error) {
      console.error('[CarbonCalculator] Load error:', error.message);
    } finally {
      setCalculating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2d5a4d] to-[#1a4038] p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">Estimer ma Rémunération PSE</h2>
                <p className="text-white/80 text-sm">Services Écosystémiques · estimation en 2 minutes</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              data-testid="close-calculator"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
          <div className="flex gap-2 mt-6">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`flex-1 h-2 rounded-full transition-all ${
                  s <= step ? 'bg-[#d4a574]' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Step 1: Basic Info + Tree Categories */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Informations sur votre exploitation
              </h3>
              
              <div>
                <Label htmlFor="hectares" className="text-gray-700">
                  Surface cultivee (hectares)
                </Label>
                <Input
                  id="hectares"
                  type="number"
                  placeholder="Ex: 5"
                  value={formData.hectares}
                  onChange={(e) => setFormData({...formData, hectares: e.target.value})}
                  className="mt-1"
                  min="0.1"
                  step="0.1"
                  data-testid="input-hectares"
                />
              </div>

              {/* Tree Height Categories */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Ruler className="w-4 h-4 text-[#2d5a4d]" />
                  <Label className="text-gray-700 font-medium">Especes d'arbres par strate</Label>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-2">
                      <TreePine className="w-5 h-5 text-emerald-700" />
                      <span className="text-xs font-semibold text-emerald-700">STRATE 3</span>
                    </div>
                    <p className="text-[10px] text-emerald-600 mb-2">Canopee &gt; 30 m</p>
                    <Input
                      type="number"
                      placeholder="0"
                      value={formData.arbres_grands}
                      onChange={(e) => setFormData({...formData, arbres_grands: e.target.value})}
                      className="h-9 text-center bg-white border-emerald-300"
                      min="0"
                      data-testid="input-arbres-grands"
                    />
                    <p className="text-[9px] text-emerald-700 mt-2 mb-1 font-medium">Ø couronne (m)</p>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.diametre_grands_m}
                      onChange={(e) => setFormData({...formData, diametre_grands_m: e.target.value})}
                      className="h-8 text-center bg-white/70 border-emerald-200 text-[11px]"
                      min="0.5"
                      max="30"
                      data-testid="input-diametre-grands"
                    />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-2">
                      <TreePine className="w-4 h-4 text-amber-700" />
                      <span className="text-xs font-semibold text-amber-700">STRATE 2</span>
                    </div>
                    <p className="text-[10px] text-amber-600 mb-2">Canopee 5 - 30 m</p>
                    <Input
                      type="number"
                      placeholder="0"
                      value={formData.arbres_moyens}
                      onChange={(e) => setFormData({...formData, arbres_moyens: e.target.value})}
                      className="h-9 text-center bg-white border-amber-300"
                      min="0"
                      data-testid="input-arbres-moyens"
                    />
                    <p className="text-[9px] text-amber-700 mt-2 mb-1 font-medium">Ø couronne (m)</p>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.diametre_moyens_m}
                      onChange={(e) => setFormData({...formData, diametre_moyens_m: e.target.value})}
                      className="h-8 text-center bg-white/70 border-amber-200 text-[11px]"
                      min="0.5"
                      max="30"
                      data-testid="input-diametre-moyens"
                    />
                  </div>
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-2">
                      <TreePine className="w-3 h-3 text-sky-700" />
                      <span className="text-xs font-semibold text-sky-700">STRATE 1</span>
                    </div>
                    <p className="text-[10px] text-sky-600 mb-2">Canopee 3 - 5 m</p>
                    <Input
                      type="number"
                      placeholder="0"
                      value={formData.arbres_petits}
                      onChange={(e) => setFormData({...formData, arbres_petits: e.target.value})}
                      className="h-9 text-center bg-white border-sky-300"
                      min="0"
                      data-testid="input-arbres-petits"
                    />
                    <p className="text-[9px] text-sky-700 mt-2 mb-1 font-medium">Ø couronne (m)</p>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.diametre_petits_m}
                      onChange={(e) => setFormData({...formData, diametre_petits_m: e.target.value})}
                      className="h-8 text-center bg-white/70 border-sky-200 text-[11px]"
                      min="0.5"
                      max="30"
                      data-testid="input-diametre-petits"
                    />
                  </div>
                </div>
                {totalTrees > 0 && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Total: {totalTrees} arbres {formData.hectares ? `(${Math.round(totalTrees / Math.max(parseFloat(formData.hectares), 0.1))}/ha)` : ''}
                  </p>
                )}
                {/* Couverture d'ombrage en temps réel */}
                {liveCoverage !== null && totalTrees > 0 && (
                  <div className="mt-3 p-3 bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 rounded-lg" data-testid="live-coverage-block">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-emerald-900 flex items-center gap-1">
                        <TreePine className="w-3 h-3" /> Couverture d'ombrage estimée
                      </span>
                      <span className={`text-lg font-bold ${
                        liveCoverage >= 30 ? 'text-emerald-700' :
                        liveCoverage >= 20 ? 'text-amber-600' :
                        'text-red-600'
                      }`} data-testid="live-coverage-value">
                        {liveCoverage}%
                      </span>
                    </div>
                    <div className="w-full bg-white rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          liveCoverage >= 30 ? 'bg-emerald-500' :
                          liveCoverage >= 20 ? 'bg-amber-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(liveCoverage, 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-emerald-800 mt-1.5 leading-relaxed">
                      Formule : Σ(N × π × (Ø/2)²) / surface. Cible <strong>Rainforest Alliance Afrique Ouest ≥ 30 %</strong> · optimum cacao <strong>40-60 %</strong>.
                    </p>
                    <p className="text-[10px] text-amber-800 mt-1 italic">
                      ⚠️ Estimation brute — la couverture réelle est plus faible (chevauchement des couronnes). Pour un calcul rigoureux (Beer-Lambert + biomasse Chave 2014), utilisez le <strong>Simulateur PSE officiel</strong>.
                    </p>
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1">
                  Les especes de Strate 3 (canopee &gt; 30m) sequestrent plus de carbone (coefficient x1.0 vs x0.7 et x0.3)
                </p>
              </div>

              <div>
                <Label className="text-gray-700">Type de culture principale</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[{id: 'cacao', label: 'Cacao'}, {id: 'cafe', label: 'Cafe'}, {id: 'anacarde', label: 'Anacarde'}].map((crop) => (
                    <button
                      key={crop.id}
                      onClick={() => setFormData({...formData, cropType: crop.id})}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.cropType === crop.id
                          ? 'border-[#2d5a4d] bg-[#2d5a4d]/10 text-[#2d5a4d] font-medium'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`crop-${crop.id}`}
                    >
                      {crop.label}
                    </button>
                  ))}
                </div>
              </div>

              <Button 
                onClick={() => setStep(2)}
                className="w-full bg-[#2d5a4d] hover:bg-[#1a4038]"
                disabled={!formData.hectares || totalTrees === 0}
                data-testid="step1-next"
              >
                Continuer
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Step 2: Practices */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Vos pratiques agricoles durables
              </h3>
              <p className="text-gray-600 text-sm">
                Selectionnez les pratiques que vous appliquez
              </p>
              
              <div className="space-y-3">
                {farmingPractices.map((practice) => (
                  <button
                    key={practice.id}
                    onClick={() => togglePractice(practice.id)}
                    className={`w-full p-4 rounded-xl border-2 flex items-center justify-between transition-all ${
                      formData.practices.includes(practice.id)
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`practice-${practice.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        formData.practices.includes(practice.id)
                          ? 'border-green-500 bg-green-500'
                          : 'border-gray-300'
                      }`}>
                        {formData.practices.includes(practice.id) && (
                          <CheckCircle className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="text-left">
                        <span className="font-medium text-gray-900">{practice.label}</span>
                        <p className="text-xs text-gray-500">{practice.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Retour
                </Button>
                <Button 
                  onClick={calculatePremium}
                  className="flex-1 bg-[#2d5a4d] hover:bg-[#1a4038]"
                  disabled={calculating}
                  data-testid="calculate-btn"
                >
                  {calculating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Calcul...
                    </>
                  ) : (
                    <>
                      Estimer ma rémunération
                      <Calculator className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {step === 3 && result && (
            <div className="space-y-6">
              <div className="text-center">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
                  result.niveau_prime === 'leader' ? 'bg-amber-100' :
                  result.niveau_prime === 'bon' ? 'bg-emerald-100' :
                  result.niveau_prime === 'eligible' ? 'bg-green-100' :
                  'bg-red-100'
                }`}>
                  {result.niveau_prime === 'leader' ? (
                    <Award className="w-10 h-10 text-amber-600" />
                  ) : result.eligible ? (
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  ) : (
                    <Leaf className="w-10 h-10 text-red-600" />
                  )}
                </div>
                <h3 className="text-2xl font-bold text-gray-900" data-testid="result-title">
                  {result.niveau_prime === 'leader' ? '🏆 Leader PSE !' :
                   result.niveau_prime === 'bon' ? 'Bon niveau, continuez !' :
                   result.niveau_prime === 'eligible' ? 'Vous êtes éligible !' :
                   "Plan d'action recommandé"}
                </h3>
                <p className="text-gray-600">
                  {result.niveau_prime === 'leader'
                    ? `Excellence PSE — bonus +${result.bonus_leader_pct}% appliqué sur votre prime`
                    : result.niveau_prime === 'bon'
                      ? 'Vos pratiques sont bonnes. Atteignez 8/10 pour devenir Leader PSE (+10% bonus)'
                      : result.niveau_prime === 'eligible'
                        ? 'Votre exploitation est éligible à la rémunération PSE (Services Écosystémiques) GreenLink'
                        : `Atteignez ${result.seuil_eligibilite || 6}/10 pour devenir éligible à la prime`}
                </p>
                {result.niveau_label && (
                  <div className="mt-3">
                    <span
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white"
                      style={{ backgroundColor: result.niveau_color || '#6b7280' }}
                      data-testid="niveau-badge"
                    >
                      {result.niveau_label}
                    </span>
                  </div>
                )}
              </div>

              {/* Score */}
              <div className="bg-gradient-to-br from-[#2d5a4d] to-[#1a4038] rounded-2xl p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white/80">Votre Score PSE</span>
                  <Badge className={`text-lg px-3 py-1 ${
                    result.eligible 
                      ? 'bg-green-400/30 text-green-100' 
                      : 'bg-orange-400/30 text-orange-100'
                  }`} data-testid="score-badge">
                    {result.score}/10
                  </Badge>
                </div>
                <div className="w-full bg-white/20 rounded-full h-4 mb-2">
                  <div 
                    className={`h-4 rounded-full transition-all duration-1000 ${
                      result.eligible ? 'bg-green-400' : 'bg-orange-400'
                    }`}
                    style={{ width: `${result.score * 10}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-white/60">
                  <span>0</span>
                  <span className="text-[#d4a574]">Seuil: {result.seuil_eligibilite || 6}</span>
                  <span className="text-amber-300">Leader: 8</span>
                  <span>10</span>
                </div>
              </div>

              {/* PSE Breakdown Checklist (7 critères Guide pratique CI) */}
              {result.score_breakdown_pse && (
                <div className="bg-white rounded-xl border-2 border-emerald-200 p-4" data-testid="pse-checklist">
                  <p className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                    Détail éligibilité PSE (7 critères)
                  </p>
                  <PSEBreakdownChecklist breakdown={result.score_breakdown_pse} />
                  <p className="text-[10px] text-gray-500 mt-3 italic">
                    Guide pratique PSE Côte d&apos;Ivoire · Modalité Agroforesterie
                  </p>
                </div>
              )}

              {/* Tree Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <TreePine className="w-4 h-4 text-[#2d5a4d]" />
                  Vos especes par strate
                </p>
                <div className="grid grid-cols-3 gap-2 text-center text-sm">
                  <div className="bg-emerald-100 rounded-lg p-2">
                    <p className="text-lg font-bold text-emerald-800">{result.arbres_grands || 0}</p>
                    <p className="text-[10px] text-emerald-600">Strate 3 (&gt;30m)</p>
                  </div>
                  <div className="bg-amber-100 rounded-lg p-2">
                    <p className="text-lg font-bold text-amber-800">{result.arbres_moyens || 0}</p>
                    <p className="text-[10px] text-amber-600">Strate 2 (5-30m)</p>
                  </div>
                  <div className="bg-sky-100 rounded-lg p-2">
                    <p className="text-lg font-bold text-sky-800">{result.arbres_petits || 0}</p>
                    <p className="text-[10px] text-sky-600">Strate 1 (3-5m)</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Densite ponderee: {result.arbres_par_ha} arbres equivalents/ha
                  {result.couverture_ombragee_pct !== undefined && (
                    <> • Couverture d'ombrage : <strong className={
                      result.couverture_ombragee_pct >= 30 ? 'text-emerald-700' :
                      result.couverture_ombragee_pct >= 20 ? 'text-amber-700' :
                      'text-red-700'
                    } data-testid="result-coverage-pct">{result.couverture_ombragee_pct}%</strong></>
                  )}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Leaf className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700">CO2 sequestre</span>
                  </div>
                  <p className="text-2xl font-bold text-green-800" data-testid="co2-value">
                    {result.co2_par_ha} <span className="text-sm">t/ha/an</span>
                  </p>
                </Card>
                
                <Card className="p-4 bg-purple-50 border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TreePine className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-purple-700">Total arbres</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-800">
                    {result.total_arbres} <span className="text-sm">arbres</span>
                  </p>
                </Card>
                
                <Card className={`p-4 ${result.eligible ? 'bg-[#d4a574]/20 border-[#d4a574]' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className={`w-5 h-5 ${result.eligible ? 'text-[#c49564]' : 'text-gray-400'}`} />
                    <span className={`text-sm ${result.eligible ? 'text-[#8b6914]' : 'text-gray-500'}`}>
                      Prime marché carbone /kg
                    </span>
                  </div>
                  <p className={`text-2xl font-bold ${result.eligible ? 'text-[#8b6914]' : 'text-gray-400'}`} data-testid="prime-kg-value">
                    {result.prime_fcfa_kg} <span className="text-sm">FCFA/kg</span>
                  </p>
                </Card>
                
                <Card className={`p-4 ${result.eligible ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className={`w-5 h-5 ${result.eligible ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <span className={`text-sm ${result.eligible ? 'text-emerald-700' : 'text-gray-500'}`}>
                      Prime marché carbone /an
                    </span>
                  </div>
                  <p className={`text-2xl font-bold ${result.eligible ? 'text-emerald-800' : 'text-gray-400'}`} data-testid="prime-annual-value">
                    {(result.prime_annuelle / 1000).toFixed(0)}K <span className="text-sm">FCFA</span>
                  </p>
                </Card>
              </div>

              {/* PSE Agroforesterie officiel (Guide pratique CI) */}
              {result.pse_agroforesterie && result.pse_agroforesterie.montant_total_fcfa > 0 && (
                <div className="rounded-xl p-4 bg-gradient-to-br from-emerald-600 to-teal-700 text-white" data-testid="pse-official-block">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider opacity-80">Prime PSE Officielle</p>
                      <p className="text-xs opacity-90">Guide pratique PSE Côte d'Ivoire — Modalité Agroforesterie</p>
                    </div>
                    <TreePine className="w-6 h-6 opacity-80" />
                  </div>
                  <p className="text-3xl font-bold" data-testid="pse-official-total">
                    {(result.pse_agroforesterie.montant_total_fcfa || 0).toLocaleString('fr-FR')} <span className="text-sm font-normal opacity-80">FCFA</span>
                  </p>
                  <p className="text-xs opacity-90 mt-1">
                    Versé sur {result.pse_agroforesterie.duree_ans} ans · Moy. {(result.pse_agroforesterie.montant_annuel_moyen_fcfa || 0).toLocaleString('fr-FR')} FCFA/an
                  </p>
                  <div className="mt-2 pt-2 border-t border-white/30 text-[11px] opacity-90">
                    Estimation basée sur ~{result.pse_agroforesterie.nb_arbres_natives_estime} arbres natifs × 650 FCFA + ~{result.pse_agroforesterie.nb_arbres_exotiques_estime} exotiques × 500 FCFA.
                  </div>
                </div>
              )}

              {/* Certification Quality Compliance */}
              {result.ars_level && (
                <div className={`rounded-xl p-4 border ${
                  result.ars_level === 'Or' ? 'bg-yellow-50 border-yellow-300' :
                  result.ars_level === 'Argent' ? 'bg-slate-50 border-slate-300' :
                  result.ars_level === 'Bronze' ? 'bg-orange-50 border-orange-300' :
                  'bg-red-50 border-red-200'
                }`} data-testid="ars-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Award className={`w-5 h-5 ${
                        result.ars_level === 'Or' ? 'text-yellow-600' :
                        result.ars_level === 'Argent' ? 'text-slate-500' :
                        result.ars_level === 'Bronze' ? 'text-orange-600' :
                        'text-red-500'
                      }`} />
                      <span className="text-sm font-semibold text-gray-800">Score Pratiques Durables</span>
                    </div>
                    <Badge className={`px-2 py-0.5 text-xs font-bold ${
                      result.ars_level === 'Or' ? 'bg-yellow-200 text-yellow-800' :
                      result.ars_level === 'Argent' ? 'bg-slate-200 text-slate-700' :
                      result.ars_level === 'Bronze' ? 'bg-orange-200 text-orange-800' :
                      'bg-red-200 text-red-700'
                    }`} data-testid="ars-badge">
                      {result.ars_level === 'Or' ? 'Excellent' : result.ars_level === 'Argent' ? 'Tres Bon' : result.ars_level === 'Bronze' ? 'Bon' : 'A ameliorer'}
                    </Badge>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                    <div 
                      className={`h-2.5 rounded-full transition-all duration-1000 ${
                        result.ars_level === 'Or' ? 'bg-yellow-500' :
                        result.ars_level === 'Argent' ? 'bg-slate-400' :
                        result.ars_level === 'Bronze' ? 'bg-orange-400' :
                        'bg-red-400'
                      }`}
                      style={{ width: `${Math.min(result.ars_pct, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-500 mb-2">
                    <span>{result.ars_pct}%</span>
                    <div className="flex gap-3">
                      <span>Bon 30%</span>
                      <span>Tres Bon 55%</span>
                      <span>Excellent 80%</span>
                    </div>
                  </div>
                  {result.ars_conseil && (
                    <p className="text-xs text-gray-600 italic">{result.ars_conseil}</p>
                  )}
                </div>
              )}

              {/* Bonus Leader PSE */}
              {result.niveau_prime === 'leader' && (
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-xl p-4" data-testid="leader-bonus-block">
                  <div className="flex items-start gap-3">
                    <Award className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-amber-900 mb-1">
                        🏆 Bonus Leader PSE : +{result.bonus_leader_pct}%
                      </p>
                      <p className="text-xs text-amber-800 leading-relaxed">
                        Votre prime annuelle a été majorée de <strong>{(((result.prime_annuelle - result.prime_annuelle_brute) / 1000)).toFixed(0)}K FCFA</strong> grâce à votre engagement exemplaire (score ≥ 8/10). Continuez vos pratiques pour conserver ce statut !
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan d'action (score < 6) */}
              {!result.eligible && result.plan_action && result.plan_action.length > 0 ? (
                <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-4" data-testid="plan-action-block">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Leaf className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-red-900">
                        Votre plan d'action personnalisé
                      </p>
                      <p className="text-xs text-red-700 mt-0.5">
                        {result.plan_action.length} actions priorisées pour atteindre le seuil de {result.seuil_eligibilite}/10 et devenir éligible
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    {result.plan_action.map((a, i) => (
                      <div
                        key={`plan-${i}-${a.axe.slice(0, 20)}`}
                        className="bg-white border border-red-100 rounded-lg p-3 flex items-start gap-3"
                      >
                        <span className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold ${
                          a.priorite === 'haute' ? 'bg-red-500 text-white' :
                          a.priorite === 'moyenne' ? 'bg-amber-500 text-white' :
                          'bg-gray-400 text-white'
                        }`}>
                          {i + 1}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-xs font-bold text-gray-900">{a.axe}</p>
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                              +{a.impact_score} pts
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 leading-relaxed">{a.action}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-[11px] text-red-700 mt-3 italic">
                    💡 Astuce : commencez par les actions à priorité haute (impact maximal). Une coopérative agréée peut vous accompagner dans cette transition.
                  </p>
                </div>
              ) : !result.eligible && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="recommandations-block">
                  <p className="text-sm text-amber-800 font-medium mb-2">Pour ameliorer votre score :</p>
                  <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                    {(result.recommandations && result.recommandations.length > 0) ? (
                      result.recommandations.map((r, idx) => (
                        <li key={`reco-${idx}-${r.slice(0, 20)}`}>{r}</li>
                      ))
                    ) : (
                      <>
                        {formData.practices.indexOf('agroforesterie') === -1 && <li>Pratiquez l'agroforesterie (arbres + cultures)</li>}
                        {formData.practices.indexOf('compost') === -1 && <li>Utilisez du compost organique pour remplacer les engrais chimiques</li>}
                        {formData.practices.indexOf('zero_pesticides') === -1 && <li>Reduisez les pesticides chimiques</li>}
                        {formData.practices.indexOf('couverture_vegetale') === -1 && <li>Ajoutez une couverture vegetale du sol</li>}
                        {(parseInt(formData.arbres_grands) || 0) < 5 && <li>Plantez plus d'especes de Strate 3 (canopee &gt; 30m)</li>}
                      </>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => { setStep(1); setResult(null); }}
                  className="flex-1"
                  data-testid="recalculate-btn"
                >
                  Recalculer
                </Button>
                <Button 
                  onClick={onClose}
                  className="flex-1 bg-[#2d5a4d] hover:bg-[#1a4038]"
                  data-testid="calculator-cta-btn"
                >
                  {result.eligible ? "S'inscrire" : "Ameliorer mon score"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

/* ==================== PSE Breakdown Checklist ==================== */

const PSE_CRITERIA_META = {
  densite_arbres_ha: {
    label: 'Densité arbres/ha',
    getSuggestion: (v, pts, max) => {
      if (pts >= max) return 'Excellent — vous dépassez la cible 30-40 arbres/ha';
      if (v >= 20) return `Ajoutez ${Math.max(30 - Math.round(v), 0)} arbres/ha pour atteindre la cible optimale`;
      if (v >= 10) return `Densité faible (${v.toFixed(1)}/ha) — plantez plus d'arbres d'ombrage`;
      return `Très faible densité (${v.toFixed(1)}/ha) — minimum PSE : 20 arbres/ha ajoutés`;
    },
    fmt: (v) => `${Number(v).toFixed(1)} arbres/ha`,
  },
  multi_strates: {
    label: 'Multi-strates (min 2)',
    getSuggestion: (v, pts, max) => {
      if (v >= 3) return 'Idéal — 3 strates détectées (canopée complète)';
      if (v === 2) return 'Bon — 2 strates. Ajoutez une strate manquante pour maximiser';
      if (v === 1) return 'Une seule strate — ajoutez des arbres d\'une autre taille (canopée 5-30m ou >30m)';
      return 'Aucune strate — plantez des arbres d\'ombrage rapidement';
    },
    fmt: (v) => `${v} strate${v > 1 ? 's' : ''}`,
  },
  natives_pct_estime: {
    label: 'Espèces natives (estimé)',
    getSuggestion: (v, pts, max) => {
      if (v >= 70) return 'Excellent — natives valorisées à 650 FCFA/arbre vs 500 exo';
      if (v >= 50) return `${v.toFixed(0)}% de natives — visez ≥70% (Iroko, Framiré, Fromager, Acajou)`;
      if (v >= 30) return `Peu de natives (${v.toFixed(0)}%) — remplacez progressivement les exotiques`;
      return 'Presque aucune native — plantez Iroko, Framiré, Fromager pour maximiser la prime';
    },
    fmt: (v) => `${Number(v).toFixed(0)}%`,
  },
  surface_eligible: {
    label: 'Surface éligible (≥ 0,5 ha)',
    getSuggestion: (v, pts, max) => {
      if (v >= 0.5) return 'Surface conforme au minimum PSE';
      return `Surface trop petite (${v} ha) — minimum requis : 0,5 ha`;
    },
    fmt: (v) => `${Number(v).toFixed(2)} ha`,
  },
  zero_brulage: {
    label: 'Zéro brûlage',
    getSuggestion: (v, pts, max) => {
      if (v === 'non') return 'Parfait — vous compostez au lieu de brûler';
      if (v === 'oui') return 'CRITIQUE : arrêtez le brûlage immédiatement (obligatoire PSE)';
      return 'Non renseigné — le brûlage est disqualifiant pour le PSE';
    },
    fmt: (v) => v === 'non' ? 'Respecté' : v === 'oui' ? 'Non respecté' : '—',
  },
  zero_deforestation: {
    label: 'Zéro déforestation',
    getSuggestion: (v, pts, max) => {
      if (v === 'oui') return 'Engagement pris — conforme à l\'EUDR';
      if (v === 'non') return 'CRITIQUE : engagement zéro déforestation requis pour le PSE';
      return 'Non renseigné — cochez cet engagement pour être éligible';
    },
    fmt: (v) => v === 'oui' ? 'Engagé' : v === 'non' ? 'Non engagé' : '—',
  },
  pratiques_agroforestieres: {
    label: 'Pratiques agroforestières',
    getSuggestion: (v, pts, max) => {
      if (pts >= max) return 'Toutes les pratiques adoptées (agroforesterie, compost, couverture)';
      if (pts >= 0.3) return 'Bonnes pratiques — ajoutez compost + couverture végétale pour maximiser';
      return 'Adoptez agroforesterie, compost et couverture végétale du sol';
    },
    fmt: () => null,
  },
};

const PSEBreakdownChecklist = ({ breakdown }) => {
  const iconFor = (pts, max) => {
    const ratio = pts / max;
    if (ratio >= 1.0) return { emoji: '✅', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' };
    if (ratio >= 0.5) return { emoji: '⚠️', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' };
    return { emoji: '❌', color: 'text-red-700', bg: 'bg-red-50 border-red-200' };
  };

  return (
    <div className="space-y-2">
      {Object.entries(breakdown).map(([key, data]) => {
        const meta = PSE_CRITERIA_META[key];
        if (!meta) return null;
        const { emoji, color, bg } = iconFor(data.pts, data.max);
        const valueLabel = meta.fmt(data.value);
        const suggestion = meta.getSuggestion(data.value, data.pts, data.max);
        return (
          <div
            key={key}
            className={`border rounded-lg p-2.5 ${bg} flex items-start gap-2`}
            data-testid={`pse-criterion-${key}`}
          >
            <span className="text-base flex-shrink-0" aria-hidden>{emoji}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-800">{meta.label}</span>
                <span className={`text-[11px] font-mono font-bold ${color}`}>
                  {data.pts}/{data.max} pts
                  {valueLabel && <span className="text-gray-500 ml-1">· {valueLabel}</span>}
                </span>
              </div>
              <p className={`text-[11px] mt-0.5 ${color}`}>{suggestion}</p>
              {/* Progress bar */}
              <div className="w-full bg-white/70 rounded-full h-1 mt-1 overflow-hidden">
                <div
                  className={`h-1 rounded-full transition-all duration-500 ${
                    data.pts / data.max >= 1.0 ? 'bg-emerald-500' :
                    data.pts / data.max >= 0.5 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min((data.pts / data.max) * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CarbonCalculator;
